import type { AssignResult } from "@jpjj/solver-core/types";
import { solveAssignmentExact } from "@jpjj/solver-core/assign";
import { LEVELS } from "../game/levels";
import { state, valuesFor } from "../game/state";
import { buildMiniLedger } from "../game/board";
import { $ } from "../dom";

/* ---------- Per-day solves (the slates are fixed, so cache them) ---------- */
const solvePromises: (Promise<AssignResult> | null)[] = LEVELS.map(() => null);
const solveResults: (AssignResult | null)[] = LEVELS.map(() => null);

function solveLevel(i: number): Promise<AssignResult> {
  let pr = solvePromises[i];
  if (!pr) {
    pr = solveAssignmentExact(valuesFor(i))
      .then((res) => { solveResults[i] = res; return res; });
    solvePromises[i] = pr;
  }
  return pr;
}

/** Kick off all five solves during play, so the reveal is instant. */
export function prepareSolves(): Promise<AssignResult[]> {
  return Promise.all(LEVELS.map((_, i) => solveLevel(i)));
}

/** n! — how many distinct rosters a day allows (for honest reporting). */
function rosterCount(n: number): number {
  let f = 1;
  for (let k = 2; k <= n; k++) f *= k;
  return f;
}

/* ---------- Carousel ---------- */
let cmpIndex = 0;
let animCancel = false;
let animRunning = false;
let timers: number[] = [];
const sleep = (ms: number) =>
  new Promise<void>((res) => { const t = window.setTimeout(res, ms); timers.push(t); });
function clearTimers(): void { for (const t of timers) clearTimeout(t); timers = []; }

export function openReveal(): void {
  $("solverOverlay").classList.remove("hidden");
  $("stageView").style.display = "";
  $("finalView").style.display = "none";
  cmpIndex = 0;
  void runLevelCompare(0);
}

async function runLevelCompare(i: number): Promise<void> {
  clearTimers();
  animCancel = false;
  animRunning = true;

  const L = LEVELS[i];
  const rec = state.records[i];
  $("cmpHead").innerHTML = `Day ${i + 1} / ${LEVELS.length} — <span class="lv-mon">📯 ${L.name}</span>`;
  ($("cmpNext") as HTMLButtonElement).disabled = true;
  $("cmpGap").innerHTML = "&nbsp;";
  $("optScore").textContent = "—";

  // your side renders immediately from the best recorded roster
  buildMiniLedger($("youMini"), i, rec.assign, "you");
  $("youScore").textContent = String(rec.total);

  const status = $("solverStatus");
  status.textContent = `Pricing Day ${i + 1} with the Hungarian method…`;
  let res: AssignResult;
  try {
    res = await solveLevel(i);
  } catch {
    status.textContent = "The exact solver could not be started — reload and try again.";
    animRunning = false;
    return;
  }
  if (animCancel) return;
  const rosters = rosterCount(L.heroCount).toLocaleString("en-US");
  status.textContent = res.status === "OPTIMAL"
    ? `The Hungarian method proved the richest of ${rosters} possible rosters in ${res.tSolveMs.toFixed(1)} ms`
    : `Best roster found in ${res.tSolveMs.toFixed(1)} ms (not proven best)`;

  await animateOptimal(i, res);
  if (animCancel) return;

  finishLevel(rec.total, res);
}

/** Post the solver's roster hero by hero, the gold counting up. */
async function animateOptimal(levelIdx: number, res: AssignResult): Promise<void> {
  const v = valuesFor(levelIdx);
  const n = res.assign.length;
  for (let k = 1; k <= n; k++) {
    if (animCancel) return;
    const partial = res.assign.map((j, i) => (i < k ? j : -1));
    buildMiniLedger($("optMini"), levelIdx, partial, "opt");
    const sum = partial.reduce((s, j, i) => (j >= 0 ? s + v[i][j] : s), 0);
    $("optScore").textContent = String(sum);
    await sleep(420);
  }
}

function finishLevel(youTotal: number, res: AssignResult): void {
  animRunning = false;
  const proven = res.status === "OPTIMAL";
  const gap = res.total - youTotal;
  const gapEl = $("cmpGap");
  if (gap <= 0) {
    gapEl.textContent = proven
      ? "You matched the proven-best roster — a perfect day's ledger."
      : "You matched (or beat) the best roster the solver found.";
  } else {
    const pct = ((gap / res.total) * 100).toFixed(1);
    gapEl.textContent =
      `Your roster: ${youTotal}g · ${proven ? "proven best" : "solver's best"}: ` +
      `${res.total}g — you left ${gap}g (${pct}%) on the table.`;
  }
  const next = $("cmpNext") as HTMLButtonElement;
  next.disabled = false;
  next.textContent = cmpIndex === LEVELS.length - 1 ? "See your run ▸" : "Next day ▸";
}

export function skipCompare(): void {
  if (!animRunning) return;
  animCancel = true;
  clearTimers();
  const res = solveResults[cmpIndex];
  const rec = state.records[cmpIndex];
  if (!res) return;
  buildMiniLedger($("optMini"), cmpIndex, res.assign, "opt");
  $("optScore").textContent = String(res.total);
  finishLevel(rec.total, res);
}

export function nextCompare(): void {
  if (animRunning) { skipCompare(); return; }
  if (cmpIndex < LEVELS.length - 1) {
    cmpIndex++;
    void runLevelCompare(cmpIndex);
  } else {
    showFinal();
  }
}

function showFinal(): void {
  const youTotal = state.records.reduce((s, r) => s + r.total, 0);
  const optTotal = solveResults.reduce((s, r) => s + (r ? r.total : 0), 0);
  const allProven = solveResults.every((r) => r && r.status === "OPTIMAL");
  $("fcYou").textContent = String(youTotal);
  $("fcOpt").textContent = String(optTotal);
  $("fcSub").textContent = allProven ? "gold · proven best" : "gold · best found";
  const gap = optTotal - youTotal;
  $("fcGap").textContent = gap <= 0
    ? "A flawless week — on all five days you posted the richest possible roster."
    : `Across five days your ledger booked ${youTotal} gold. The optimizer books ${optTotal} — ` +
      `${gap} gold (+${((gap / youTotal) * 100).toFixed(1)}%) more, same heroes, same contracts.`;

  const rows = LEVELS.map((L, i) => {
    const o = solveResults[i];
    const ov = o ? o.total : 0;
    const yv = state.records[i].total;
    const left = ov - yv;
    return `<tr><td>📯 ${L.name}</td><td>${yv}</td><td class="gold">${ov}</td>` +
      `<td>${left <= 0 ? "—" : "−" + left + "g"}</td></tr>`;
  }).join("");
  $("fcTable").innerHTML =
    `<thead><tr><th>Day</th><th>You</th><th>Best</th><th>Left behind</th></tr></thead><tbody>${rows}</tbody>`;

  $("stageView").style.display = "none";
  $("finalView").style.display = "";
}

export function resetReveal(): void {
  animCancel = true;
  animRunning = false;
  clearTimers();
  cmpIndex = 0;
  $("youMini").innerHTML = "";
  $("optMini").innerHTML = "";
  $("stageView").style.display = "";
  $("finalView").style.display = "none";
}
